import { useRef, useState } from "react";
import { Check, Download, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "@/components/Reveal";
import { API, api } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { scrollToId } from "@/lib/lenis";
import { useNavigate } from "react-router-dom";

const fieldCls = "w-full bg-raised border border-slate-400/15 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-blaze/60 transition-colors";

export default function FreePage() {
  useSEO("Free Customer Intake Form — The Mechanic's Tool Kit", "Download a free, ready-to-print customer intake & work authorization form for your auto repair shop. No card required.");
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const reviewRef = useRef(null);
  const [review, setReview] = useState({ name: "", shop: "", rating: 5, text: "" });
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);

  const claim = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/free-sample", form);
      setClaimed(true);
      toast.success("Sample unlocked — download it below. A copy is on its way to your email too.");
    } catch {
      toast.error("Could not unlock the sample — try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    setReviewBusy(true);
    try {
      await api.post("/reviews", { ...review, name: review.name || form.name });
      setReviewDone(true);
      toast.success("Review received — it goes live after a quick check. Thank you.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send the review — try again.");
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <main className="pt-28 pb-24 min-h-screen blueprint-grid" data-testid="free-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <SectionHead
          overline="Free Sample // No Card Required"
          title="Take the intake form for a test drive"
          sub="One working page from the Starter Kit: the customer intake & work authorization with the pre-existing damage walk-around. Print it, use it on your next job, and see why shops buy the full system."
        />

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-6">
            <ul className="space-y-4">
              {[
                "Ready-to-print PDF — clipboard or front-desk use today",
                "The damage walk-around that kills bogus scratch claims",
                "Signed work + diagnostic fee authorization language",
                "If it saves you one argument, imagine the other 13 documents",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check className="w-5 h-5 text-blaze shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <div className="mt-10 border border-slate-400/10 bg-panel p-7">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-blaze">The deal</p>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                The sample is free in exchange for your email — and if it earns its keep, a one-line review
                below. That's the whole catch.
              </p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={0.12}>
            <div className="clip-notch border border-slate-400/15 bg-panel p-8">
              {!claimed ? (
                <form onSubmit={claim} className="space-y-5">
                  <div>
                    <label htmlFor="free-name" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Your name</label>
                    <input id="free-name" data-testid="free-name-input" required value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} placeholder="Sam Torque" />
                  </div>
                  <div>
                    <label htmlFor="free-email" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Email</label>
                    <input id="free-email" data-testid="free-email-input" required type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} className={fieldCls} placeholder="sam@yourshop.com" />
                  </div>
                  <button data-testid="free-submit-button" disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2.5 bg-blaze text-white font-display font-bold uppercase tracking-wide text-base px-6 py-4 clip-notch-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(249,115,22,0.4)] disabled:opacity-60">
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                    Unlock the free sample
                  </button>
                  <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-slate-600 text-center">
                    No card. No account. Unsubscribe-free — one email, that's it.
                  </p>
                </form>
              ) : (
                <div data-testid="free-download-panel">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-green-400">Sample unlocked</p>
                  <a data-testid="free-download-button" href={`${API}/free-sample/download`}
                    onClick={() => {
                      setDownloaded(true);
                      setTimeout(() => reviewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 350);
                    }}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2.5 bg-blaze text-white font-display font-bold uppercase tracking-wide text-base px-6 py-4 clip-notch-sm transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(249,115,22,0.4)]">
                    <Download className="w-5 h-5" /> Download the intake form (PDF)
                  </a>

                  {!reviewDone ? (
                    <form ref={reviewRef} onSubmit={submitReview}
                      className={`mt-8 pt-6 space-y-4 border rounded-sm p-4 -mx-4 transition-colors duration-500 ${downloaded ? "border-blaze/60 bg-blaze/5" : "border-slate-400/10 border-t-slate-400/10"}`}>
                      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-blaze font-semibold">One favor in exchange — leave a one-line review</p>
                      <div className="flex gap-1.5" data-testid="review-rating">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} type="button" data-testid={`review-star-${n}`}
                            onClick={() => setReview({ ...review, rating: n })}
                            aria-label={`${n} stars`}
                            className="p-1 transition-transform duration-150 hover:scale-110">
                            <Star className={`w-6 h-6 ${n <= review.rating ? "text-blaze fill-blaze" : "text-slate-600"}`} />
                          </button>
                        ))}
                      </div>
                      <input data-testid="review-shop-input" value={review.shop} required maxLength={80}
                        onChange={(e) => setReview({ ...review, shop: e.target.value })}
                        className={fieldCls} placeholder="Your shop (e.g. Laux Mobile Mechanic LLC)" />
                      <textarea data-testid="review-text-input" value={review.text} required maxLength={400} rows={3}
                        onChange={(e) => setReview({ ...review, text: e.target.value })}
                        className={`${fieldCls} resize-none`} placeholder="One honest line — did the form earn its keep?" />
                      <button data-testid="review-submit-button" disabled={reviewBusy}
                        className="inline-flex items-center gap-2 border border-blaze/50 text-blaze font-display font-bold uppercase tracking-wide text-sm px-6 py-3 transition-colors duration-200 hover:bg-blaze hover:text-white disabled:opacity-60">
                        {reviewBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post the review"}
                      </button>
                    </form>
                  ) : (
                    <p data-testid="review-thanks" className="mt-8 pt-6 border-t border-slate-400/10 text-sm text-green-400">
                      Review received — it appears on the site after a quick check. You're good people.
                    </p>
                  )}

                  <button data-testid="free-upsell-button" onClick={() => { navigate("/"); setTimeout(() => scrollToId("kits"), 350); }}
                    className="mt-8 w-full text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-blaze transition-colors">
                    Ready for all 14 documents? See the kits →
                  </button>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
